/**
 * tourMenuOpen — lets the GuidedTour engine hold the main menu open while its
 * menu step is on screen.
 *
 * Why this exists
 * ---------------
 * Every other tour step spotlights something that is already on the page. The
 * menu is the one thing in the app that does not exist until you tap it, and a
 * ring drawn around a closed three-line button teaches nothing: the whole point
 * of the step is that there is a LIST in there. So the tour opens it.
 *
 * Why a module store rather than a prop or context. MainMenu is rendered by
 * AppShell's header on every page, and GuidedTour is mounted somewhere else
 * entirely; threading a prop between them would mean AppShell knowing about the
 * tour. Same shape and same reasoning as [[guidedTourScroll]], with a subscribe
 * added because this one has to re-render a component rather than just be read
 * at the right moment.
 *
 * The tour owns the whole lifetime while the flag is set: MainMenu ignores
 * Radix's own open/close requests, so nothing (an outside tap, Escape, the
 * scrim) can dismiss the panel out from under the step. It closes when the flag
 * clears, which is when the step changes or the tour ends.
 */
let open = false;
const listeners = new Set<() => void>();

/** Called by the GuidedTour engine: true while a step wants the menu open. */
export function setTourMenuOpen(value: boolean): void {
  if (open === value) return;
  open = value;
  for (const listener of listeners) listener();
}

/** getSnapshot for useSyncExternalStore. */
export function isTourMenuOpen(): boolean {
  return open;
}

/** subscribe for useSyncExternalStore. Returns the unsubscriber. */
export function subscribeTourMenuOpen(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
